/**
 * TreeMapCard Molecule
 *
 * Displays a single sphere in the tree map with drag-drop support
 */

import React, { useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'localization';
import type { TreeMapCardProps } from '../../organisms/TreeMap/types';
import { resolveLevelConfig, VISIBLE_PROPERTIES_LIMIT_BY_VIEW } from '../../organisms/TreeMap/defaults';
import type { GlyphColor } from '../../../types/glyph';

/**
 * Capitalize the first letter of each word in a string
 */
function toTitleCase(str: string): string {
  return str.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import type { SphereStatusValue } from '../sphere-avatar/sphere-avatar';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import StatusPopoverMolecule from '../Atlas/StatusPopover/StatusPopoverMolecule';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import type { StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import './TreeMapCard.scss';

const URGENCY_TONE: Record<string, StatusBadgeTone> = {
  critical: 'danger',
  high: 'warn',
  medium: 'warn',
  low: 'success',
};

const URGENCY_LABEL_KEY: Record<string, string> = {
  critical: 'treeMap.card.urgency.critical',
  high: 'treeMap.card.urgency.high',
  medium: 'treeMap.card.urgency.medium',
  low: 'treeMap.card.urgency.low',
};

const TreeMapCard: React.FC<TreeMapCardProps> = ({
  sphere,
  level,
  viewConfig,
  baseConfig,
  cardView,
  editable,
  isSelected,
  isDragging,
  onSelect,
  onExpand,
  onEdit,
  onDelete,
  onDragStart,
  onDragEnd,
  schemas,
  sphereLookup,
}) => {
  const { t } = useTranslation();

  // Ref for status popover
  const statusPopoverRef = useRef<OverlayPanel>(null);

  // Handle status click to open popover
  const handleStatusClick = (e: React.MouseEvent) => {
    console.log('[TreeMapCard] handleStatusClick called', {
      sphereId: sphere.id,
      hasRef: !!statusPopoverRef.current,
      event: e
    });
    e.stopPropagation(); // Don't trigger card select
    statusPopoverRef.current?.toggle(e);
  };

  // Resolve this column's effective display config (level override → view/base config → defaults).
  const resolvedConfig = resolveLevelConfig({ base: baseConfig, view: viewConfig }, level.id);
  const visibleProperties = resolvedConfig.visibleProperties;
  const showStatus = resolvedConfig.showStatus;
  const showFlow = resolvedConfig.showFlow;
  const showAvatar = resolvedConfig.showAvatar;

  // Get glyph and image from sphere
  const glyph = sphere.glyph ?? 'default-avatar';
  const glyphColor = sphere.glyphColor;
  const imageId = Array.isArray(sphere.properties.imageId)
    ? sphere.properties.imageId[0] || null
    : sphere.properties.imageId || null;

  // Handle drag start
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({
      sphereId: sphere.id,
      schemaType: sphere.schema_type,
    }));
    onDragStart?.();
  };

  // Handle drag end
  const handleDragEnd = (e: React.DragEvent) => {
    onDragEnd?.();
  };

  // Render field value
  const renderFieldValue = (fieldName: string) => {
    const value = sphere.properties[fieldName];
    if (value === null || value === undefined) return null;

    // Handle different field types
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Get urgency tone (for StatusBadgeAtom) and label
  const getUrgencyInfo = (): { tone: StatusBadgeTone; label: string } | null => {
    const urgency = sphere.properties.urgency;
    if (!urgency) return null;

    const key = String(urgency).toLowerCase();
    const labelKey = URGENCY_LABEL_KEY[key];

    return {
      tone: URGENCY_TONE[key] || 'neutral',
      label: labelKey ? t(labelKey) : String(urgency),
    };
  };

  // Get workflow status if available
  const getWorkflowStatus = () => {
    const stageId = sphere.properties.currentStageId;
    const stageName = sphere.properties.currentStageName;
    if (!stageId && !stageName) return null;

    return stageName || `Stage ${stageId}`;
  };

  // Get arc health data if available (expanded mode)
  const getArcHealth = () => {
    if (!sphere.flow?.stages || sphere.flow.stages.length === 0) {
      return null;
    }

    const stages = sphere.flow.stages;
    const completedStages = stages.filter((s: any) => s.status === 'completed').length;
    const inProgressStages = stages.filter((s: any) => s.status === 'in_progress').length;
    const notStartedStages = stages.filter((s: any) => s.status === 'not_started').length;

    // Calculate health percentage
    const healthPercent = Math.round((completedStages / stages.length) * 100);

    // Determine health tier (drives the SCSS color token, see TreeMapCard.scss)
    let healthTier: 'good' | 'moderate' | 'poor' = 'good';
    if (healthPercent < 30) {
      healthTier = 'poor';
    } else if (healthPercent < 70) {
      healthTier = 'moderate';
    }

    return {
      totalStages: stages.length,
      completed: completedStages,
      inProgress: inProgressStages,
      notStarted: notStartedStages,
      healthPercent,
      healthTier,
    };
  };

  // Get sphere status point indicator (from sphere.status point)
  // Returns a SphereStatusValue for the SphereAvatar sparkle
  const getSphereStatus = (): SphereStatusValue => {
    const statusPoint = sphere.status;
    const statusValue = statusPoint?.currentValue?.status;

    if (!statusValue) {
      return 'unknown';
    }

    // Valid status values
    const validStatuses: SphereStatusValue[] = [
      'on_track', 'at_risk', 'needs_attention', 'critical', 'blocked', 'completed', 'unknown'
    ];

    return validStatuses.includes(statusValue as SphereStatusValue)
      ? (statusValue as SphereStatusValue)
      : 'unknown';
  };

  // Get relationship perspectives - perspectives on edges FROM parent TO this child
  // These are the system names like 'center_parent', 'center_responsible', etc.
  const getRelationshipPerspectives = (limit?: number) => {
    const relationshipPerspectives = sphere.relationshipPerspectives || [];

    if (relationshipPerspectives.length === 0) return null;

    // Get perspective labels from center_abstract schema if available
    const getPerspectiveLabel = (systemName: string): string => {
      // Look for center_abstract schema which defines base perspectives
      const abstractSchema = schemas?.find((s: any) => s.system_name === 'center_abstract');
      if (abstractSchema?.perspectives) {
        const perspective = abstractSchema.perspectives.find((p: any) => p.system_name === systemName);
        if (perspective?.label || perspective?.name) {
          return perspective.label || perspective.name;
        }
      }

      // Fallback: format system_name as readable label
      return toTitleCase(systemName.replaceAll('center_', '').replaceAll('_', ' '));
    };

    const perspectiveNames = relationshipPerspectives.map((systemName: string) => getPerspectiveLabel(systemName));

    const items = limit ? perspectiveNames.slice(0, limit) : perspectiveNames;
    return items;
  };

  // Get assigned spheres from perspectives object
  const getAssignedSpheres = () => {
    const assignments: Array<{
      label: string;
      assignees: Array<{ id: string; name: string; glyph?: string; glyphColor?: GlyphColor; imageId?: string }>;
    }> = [];
    const perspectivesData = sphere.perspectives || {};

    // Get perspective label from center_abstract schema or fallback map
    const getPerspectiveLabel = (systemName: string): string | null => {
      // Look for center_abstract schema which defines base perspectives
      const abstractSchema = schemas?.find((s: any) => s.system_name === 'center_abstract');
      if (abstractSchema?.perspectives) {
        const perspective = abstractSchema.perspectives.find((p: any) => p.system_name === systemName);
        if (perspective?.label || perspective?.name) {
          return perspective.label || perspective.name;
        }
      }

      // Fallback labels for known perspectives
      const fallbackLabels: Record<string, string> = {
        'center_center': 'Center',
        'center_parent': 'Parent',
        'center_responsible': 'Responsible',
        'center_accountable': 'Accountable',
        'center_consulted': 'Consulted',
        'center_informed': 'Informed',
      };
      return fallbackLabels[systemName] || null;
    };

    // Lookup assignee info from sphereLookup
    const getAssigneeInfo = (assigneeId: string) => {
      const assignee = sphereLookup?.[assigneeId];
      if (assignee) {
        return {
          id: assigneeId,
          name: assignee.name || assigneeId,
          glyph: assignee.glyph,
          glyphColor: assignee.glyphColor,
          imageId: assignee.imageId
        };
      }
      return { id: assigneeId, name: assigneeId };
    };

    for (const [perspKey, assigneeIds] of Object.entries(perspectivesData)) {
      const label = getPerspectiveLabel(perspKey);
      if (label && Array.isArray(assigneeIds) && assigneeIds.length > 0) {
        const assignees = assigneeIds.map(id => getAssigneeInfo(id));
        assignments.push({ label, assignees });
      }
    }

    return assignments.length > 0 ? assignments : null;
  };

  // Get sphere info by ID - returns looked up data if available, otherwise fallback to ID as name
  const getSphereInfo = (id: string | undefined | null) => {
    if (!id || typeof id !== 'string') return null;

    const info = sphereLookup?.[id];
    return {
      id,
      name: info?.name || id,
      glyph: info?.glyph,
      glyphColor: info?.glyphColor,
      imageId: info?.imageId
    };
  };

  // Get createdBy/updatedBy IDs
  const createdById = sphere.properties?.createdBy || sphere.properties?.created_by;
  const updatedById = sphere.properties?.updatedBy || sphere.properties?.updated_by;

  // Format relative time
  const getRelativeTime = (timestamp: string | number) => {
    if (!timestamp) return null;

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Get additional metadata fields for expanded view (excluding common/internal fields)
  const getAdditionalMetadata = () => {
    const excludeFields = new Set([
      'name', 'title', 'description', 'status', 'urgency', 'active',
      'created_at', 'updated_at', 'created_by', 'updated_by',
      'currentStageId', 'currentStageName', 'imageId', 'type',
      'id', 'organization_id', 'schema_type', 'parent_id',
      'sphereId', 'schema_id', 'schemaId', 'sphere_id',
      'versionId', 'version_id', 'versionid',
      ...visibleProperties // Also exclude fields already shown by visibleProperties
    ]);

    const metadata: Array<{ key: string; label: string; value: string }> = [];

    for (const [key, value] of Object.entries(sphere.properties)) {
      if (excludeFields.has(key)) continue;
      if (value === null || value === undefined || value === '') continue;

      // Format the key as a label
      const label = toTitleCase(key.replaceAll('_', ' '));
      const displayValue = renderFieldValue(key);

      if (displayValue) {
        metadata.push({ key, label, value: displayValue });
      }
    }

    return metadata.length > 0 ? metadata.slice(0, 5) : null; // Limit to 5 fields
  };

  // Renders the author's ordered visibleProperties list, capped per density.
  // Uniform across all three densities — no field is special-cased as "the body".
  const renderVisibleProperties = () => {
    const limit = VISIBLE_PROPERTIES_LIMIT_BY_VIEW[cardView] ?? VISIBLE_PROPERTIES_LIMIT_BY_VIEW.standard;
    const rows = visibleProperties
      .slice(0, limit)
      .map((fieldName) => ({ fieldName, value: renderFieldValue(fieldName) }))
      .filter((row): row is { fieldName: string; value: string } => row.value !== null);

    if (rows.length === 0) return null;

    return (
      <div className="tree-map-card__fields">
        {rows.map(({ fieldName, value }) => (
          <div key={fieldName} className="tree-map-card__field">
            <span className="tree-map-card__field-value">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Renders the urgency + workflow badges, gated by showStatus / showFlow respectively.
  const renderStatusBadges = () => {
    const urgencyInfo = showStatus ? getUrgencyInfo() : null;
    const workflowStatus = showFlow ? getWorkflowStatus() : null;

    if (!urgencyInfo && !workflowStatus) return null;

    return (
      <div className="tree-map-card__badges">
        {urgencyInfo && <StatusBadgeAtom tone={urgencyInfo.tone} label={urgencyInfo.label} size="small" />}
        {workflowStatus && <StatusBadgeAtom tone="info" label={workflowStatus} size="small" />}
      </div>
    );
  };

  return (
    <button
      type="button"
      className={`tree-map-card tree-map-card--${cardView} ${isSelected ? 'tree-map-card--selected' : ''} ${isDragging ? 'tree-map-card--dragging' : ''}`}
      draggable={editable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={onSelect}
    >
      {/* Card Header */}
      <div className="tree-map-card__header">
        <div className="tree-map-card__title">
          {showAvatar && (
            <SphereAvatar
              glyph={glyph}
              glyphColor={glyphColor}
              sphereId={sphere.id}
              imageId={imageId}
              size="small"
              className="tree-map-card__avatar"
              status={showStatus ? getSphereStatus() : undefined}
              onStatusClick={showStatus ? handleStatusClick : undefined}
            />
          )}
          <span className="tree-map-card__name">
            {sphere.properties?.['is_private'] && (
              <i className="pi pi-lock" style={{ marginRight: '6px', color: 'var(--primary-color, #3B82F6)', fontSize: '0.75rem' }} />
            )}
            {sphere.properties.name || sphere.properties.title || 'Untitled'}
          </span>
        </div>

        {/* Show expand button when handler is provided */}
        {onExpand && (
          <div className="tree-map-card__actions">
            <ButtonAtom
              icon="pi-arrow-right"
              variant="secondary"
              size="small"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onExpand();
              }}
              tooltip="Expand"
            />
          </div>
        )}
      </div>

      {/* Status Popover */}
      <StatusPopoverMolecule
        ref={statusPopoverRef}
        sphereId={sphere.id}
        sphereName={sphere.properties.name || sphere.properties.title || 'Untitled'}
        schemaType={sphere.properties?.type}
        status={sphere.status}
      />

      {/* Card Body - the ordered visibleProperties list renders at every density,
          capped by VISIBLE_PROPERTIES_LIMIT_BY_VIEW; no field is special-cased. */}
      <div className="tree-map-card__body">
        {renderVisibleProperties()}

        {cardView !== 'minimal' && (
          <>
            {renderStatusBadges()}

            {/* Perspective assignments (limit to 2 at standard, full list at expanded) */}
            {getRelationshipPerspectives(cardView === 'standard' ? 2 : undefined) && (
              <div className="tree-map-card__perspectives">
                <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>Relationship:</span>
                {getRelationshipPerspectives(cardView === 'standard' ? 2 : undefined)!.map((name: string) => (
                  <span
                    key={name}
                    className="tree-map-card__perspective-badge"
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                    }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {/* Expanded View: additional details beyond standard */}
        {cardView === 'expanded' && (
          <>
            {/* Assigned spheres */}
            {getAssignedSpheres() && (
              <div className="tree-map-card__assignments">
                {getAssignedSpheres()!.map((assignment) => (
                  <div key={assignment.label} className="tree-map-card__assignment-row">
                    <span className="tree-map-card__assignment-label">{assignment.label}:</span>
                    <div className="tree-map-card__assignment-values">
                      {assignment.assignees.slice(0, 2).map((assignee) => (
                        <SphereIdentityAtom
                          key={assignee.id}
                          sphereId={assignee.id}
                          sphereName={assignee.name}
                          glyph={assignee.glyph}
                          glyphColor={assignee.glyphColor}
                          imageId={assignee.imageId}
                          size="small"
                          className="tree-map-card__assignee"
                        />
                      ))}
                      {assignment.assignees.length > 2 && (
                        <span className="tree-map-card__more-assignees">
                          {t('treeMap.card.moreAssignees', { count: assignment.assignees.length - 2 })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Additional sphere properties not in visibleProperties */}
            {getAdditionalMetadata() && (
              <div className="tree-map-card__extra-metadata">
                {getAdditionalMetadata()!.map(({ key, label, value }) => (
                  <div key={key} className="tree-map-card__extra-metadata-row">
                    <span className="tree-map-card__extra-metadata-label">{label}:</span> {value}
                  </div>
                ))}
              </div>
            )}

            {/* Arc Health (if available) */}
            {showFlow && getArcHealth() && (
              <div className="tree-map-card__flow-health">
                <div className="tree-map-card__flow-health-label">{t('treeMap.card.flowProgress')}</div>
                <div className="tree-map-card__flow-health-row">
                  <div className="tree-map-card__flow-health-bar">
                    <div
                      className={`tree-map-card__flow-health-bar-fill tree-map-card__flow-health-bar-fill--${getArcHealth()!.healthTier}`}
                      style={{ width: `${getArcHealth()!.healthPercent}%` }}
                    />
                  </div>
                  <span className={`tree-map-card__flow-health-percent tree-map-card__flow-health-percent--${getArcHealth()!.healthTier}`}>
                    {getArcHealth()!.healthPercent}%
                  </span>
                </div>
                <div className="tree-map-card__flow-health-summary">
                  {t('treeMap.card.flowSummary', {
                    completed: getArcHealth()!.completed,
                    inProgress: getArcHealth()!.inProgress,
                    notStarted: getArcHealth()!.notStarted,
                  })}
                </div>
              </div>
            )}

            {/* Created/Updated info */}
            {(createdById || updatedById) && (
              <div className="tree-map-card__audit-info">
                {getSphereInfo(createdById) && (
                  <div className="tree-map-card__audit-row">
                    <span className="tree-map-card__audit-label">
                      Created{sphere.properties.created_at ? ` ${getRelativeTime(sphere.properties.created_at)}` : ''}:
                    </span>
                    <SphereIdentityAtom
                      sphereId={getSphereInfo(createdById)!.id}
                      sphereName={getSphereInfo(createdById)!.name}
                      glyph={getSphereInfo(createdById)!.glyph}
                      glyphColor={getSphereInfo(createdById)!.glyphColor}
                      imageId={getSphereInfo(createdById)!.imageId}
                      size="small"
                      className="tree-map-card__audit-user"
                    />
                  </div>
                )}
                {getSphereInfo(updatedById) && updatedById !== createdById && (
                  <div className="tree-map-card__audit-row">
                    <span className="tree-map-card__audit-label">
                      Updated{sphere.properties.updated_at ? ` ${getRelativeTime(sphere.properties.updated_at)}` : ''}:
                    </span>
                    <SphereIdentityAtom
                      sphereId={getSphereInfo(updatedById)!.id}
                      sphereName={getSphereInfo(updatedById)!.name}
                      glyph={getSphereInfo(updatedById)!.glyph}
                      glyphColor={getSphereInfo(updatedById)!.glyphColor}
                      imageId={getSphereInfo(updatedById)!.imageId}
                      size="small"
                      className="tree-map-card__audit-user"
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </button>
  );
};

export default TreeMapCard;
